import React, { FC } from 'react';
import { SCORE_OPTIONS } from './constants';

const scaleDot = (selected: boolean, readOnly?: boolean) =>
    `w-[46px] h-[44px] rounded-[10px] border-[1.5px] text-[15px] font-semibold transition-all max-[560px]:w-10 max-[560px]:h-[42px] ${
        readOnly ? 'cursor-default' : 'cursor-pointer'
    } ${
        selected
            ? 'bg-[#C8A97E] border-[#C8A97E] text-white shadow-[0_2px_8px_rgba(200,169,126,0.35)]'
            : `bg-surface border-border-token text-text-2 ${
                readOnly ? '' : 'hover:border-[#C8A97E] hover:bg-[#FDFBF7] dark:hover:bg-surface-2'
            }`
    }`;

const miniDot = (selected: boolean, readOnly?: boolean) =>
    `w-[30px] h-[30px] rounded-[7px] border-[1.5px] text-[12.5px] font-semibold transition-all max-[560px]:w-[34px] max-[560px]:h-[34px] ${
        readOnly ? 'cursor-default' : 'cursor-pointer'
    } ${
        selected
            ? 'bg-[#C8A97E] border-[#C8A97E] text-white'
            : `bg-surface border-border-token text-muted ${
                readOnly ? '' : 'hover:border-[#C8A97E]'
            }`
    }`;

interface ScaleProps {
    value: number | null;
    left: string;
    right: string;
    onChange: (value: number) => void;
    readOnly?: boolean;
}

export const RatingScale: FC<ScaleProps> = ({
    value,
    left,
    right,
    onChange,
    readOnly,
}) => (
    <div className="inline-block">
        <div className="flex gap-2">
            {SCORE_OPTIONS.map((score) => (
                <button
                    key={score}
                    type="button"
                    aria-pressed={value === score}
                    aria-label={`${score} of 5`}
                    className={scaleDot(value === score, readOnly)}
                    disabled={readOnly}
                    onClick={() => onChange(score)}
                >
                    {score}
                </button>
            ))}
        </div>
        <div className="flex justify-between mt-[7px] text-[11.5px] text-muted font-medium max-w-[278px] max-[560px]:max-w-[240px]">
            <span>{left}</span>
            <span>{right}</span>
        </div>
    </div>
);

interface RowProps {
    value: number | null;
    onChange: (value: number) => void;
    readOnly?: boolean;
}

export const RatingRow: FC<RowProps> = ({ value, onChange, readOnly }) => (
    <div className="flex gap-[5px] shrink-0">
        {SCORE_OPTIONS.map((score) => (
            <button
                key={score}
                type="button"
                aria-pressed={value === score}
                aria-label={`${score} of 5`}
                className={miniDot(value === score, readOnly)}
                disabled={readOnly}
                onClick={() => onChange(score)}
            >
                {score}
            </button>
        ))}
    </div>
);

export const RatingValueInline: FC<{ value: number | null }> = ({ value }) =>
    value == null ? (
        <span className="text-[12.5px] text-[#B5AFA5] dark:text-muted shrink-0">—</span>
    ) : (
        <span className="inline-flex items-center justify-center min-w-[34px] h-[26px] px-2 rounded-md bg-[#C8A97E] text-white text-[12.5px] font-bold shrink-0">
            {`${value}/5`}
        </span>
    );
